/*eslint no-magic-numbers: ["error", { "ignore": [0,1,2] }]*/
import React, { useEffect, useRef, useState } from 'react'
import { useHandleZip } from '../../../hooks/useHandleZip'
import { useNavigate, useParams } from 'react-router-dom'
import { usePagination } from '../../../hooks/usePagination'
import sortDirection from '../../../constants/sortDirection'
import { getSiteForDownload, getSitesReportsRequest } from '../../../api/reports'
import qs from 'query-string'
import { showErrorToast } from '../../../utils/toast'
import ExpandedReportsTable from './ExpandedReportsTable'
import { makeSiteDetailsTabColumns, makeSiteDetailsTabRow } from './siteDetailsTabTable'
import Table from '../../../components/Table'
import {
  Dash,
  ReportsPaginationWrapper,
  ReportsTableWrapper,
  ReportTableTitle,
  ReportTableWrapper,
  RightButtons
} from '../style'
import ItemsPerPageDropdown from '../../../components/ItemsPerPageDropdown'
import Pagination from '../../../features/Pagination'
import { FormattedMessage } from 'react-intl'
import Button from '../../../components/Button'
import Download from '../../../assets/icons/download-icon'
import Expand from '../../../assets/icons/expand-icon'
import { buildReportsExpandPath } from '../../../routing/constants'
import { getUrlForAllFilters } from '../index'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 10,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const defaultFilters = {
  siteFilters: {
    region: [],
    country: [],
    system: [],
    threats: [],
    assessmentStatus: [],
    kbaStatus: [],
    kbaClassification: [],
    kbaCriterias: [],
    dateOfMostRecentAssessment: [],
    url: ''
  },
  speciesFilters: {
    speciesList: [],
    classList: [],
    selectedSpecieOptions: []
  },
  ecosystemFilters: {
    ecosystemList: [],
    ecosystemRedListCategory: [],
    url: ''
  },
  ecologicalFilters: {
    ecoregionList: [],
    url: ''
  },
  showReports: false
}

const SitesReportTab = ({
  filters,
  setFilters,
  setLoadingScreenMessage,
  isSitesLoading,
  setIsSiteLoading,
  isRunReportTriggered
  
}) => {
  const { downloadZip } = useHandleZip()
  const history = useNavigate()
  const params = useParams()
  const { pagination, setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } =
    usePagination(defaultPaginationState)
  const [sitesReports, setSiteReports] = useState([])
  const [siteSorting, setSiteSorting] = useState({
    sortBy: 'siteCode',
    sortDirection: sortDirection.ASC
  })
  const prevRunReportTriggered = useRef(isRunReportTriggered)

  const getSitesReports = async () => {
    if (isSitesLoading) {
      return
    }
    const queryString = getUrlForAllFilters(filters)
    const sorting = {
      sortBy: siteSorting.sortBy,
      sortDirection: siteSorting.sortDirection
    }
    const sitePagination = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage
    }
    setIsSiteLoading(true)

    try {
      const response = await getSitesReportsRequest({
        queryString,
        pagination: qs.stringify(sitePagination),
        sorting: qs.stringify(sorting)
      })

      if (response.error) {
        setIsSiteLoading(false)
        setSiteReports([])
        setPagination(defaultPaginationState)
        localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
        setFilters(defaultFilters)
        return showErrorToast(response.error, 'showBorder')
      }

      setSiteReports(response.sites)
      setPagination(response.pagination)
      setIsSiteLoading(false)

    } catch (err) {
      setIsSiteLoading(false)
      setSiteReports([])
      setPagination(defaultPaginationState)
      localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
      setFilters(defaultFilters)
      return showErrorToast('Something went wrong!', 'showBorder')
    }
  }

  useEffect(() => {
    if (!filters.showReports) {
      return
    }

    const runTriggered = prevRunReportTriggered.current !== isRunReportTriggered
    prevRunReportTriggered.current = isRunReportTriggered

    // Re-running with new filters must start at page 1 (stale page → empty table).
    if (runTriggered && pagination.currentPage !== 1) {
      setPagination((prev) => {
        return { ...prev, currentPage: 1 }
      })
      return
    }

    getSitesReports()
  }, [
    pagination.currentPage,
    pagination.resultsPerPage,
    siteSorting.sortBy,
    siteSorting.sortDirection,
    isRunReportTriggered
  ])

  const downloadCSV = async () => {
    setLoadingScreenMessage('buildingReport')
    setIsSiteLoading(true)
    const queryString = getUrlForAllFilters(filters)

    try {
      const response = await getSiteForDownload(queryString)

      if (response.error) {
        showErrorToast('something go wrong')
        setLoadingScreenMessage('runningReport')
        return setIsSiteLoading(false)
      }

      setIsSiteLoading(false)
      setLoadingScreenMessage('runningReport')
      return downloadZip(JSON.parse(response.join('')).flat(), 'Site_Details_report_')

    } catch (err) {
      setLoadingScreenMessage('runningReport')
      setIsSiteLoading(false)
      showErrorToast(err)
    }
  }

  const expandTable = () => {
    history(buildReportsExpandPath('siteDetails'))
  }

  if (params.title && sitesReports.length !== 0) {
    return <ExpandedReportsTable
      title={params.title}
      loading={isSitesLoading}
      rows={makeSiteDetailsTabRow(sitesReports)}
      columns={makeSiteDetailsTabColumns(siteSorting, setSiteSorting, sitesReports)}
      totalNumber={pagination.totalNumber}
      resultsPerPage={pagination.resultsPerPage}
      changeResultsPerPage={changeResultsPerPage}
      download={downloadCSV}
    />
  }


  return (
    <>
      <ReportTableWrapper>
        <ReportTableTitle>
          <FormattedMessage id='Site Details' />
          <Dash>{'—'}</Dash>
          <FormattedMessage id='reports' />
        </ReportTableTitle>
        <RightButtons>
          <Button dataTest='download' withIcon={true} height='32px' marginTop='0px' onClick={downloadCSV}>
            <Download />
            <FormattedMessage id='download' />
          </Button>
          <Button dataTest='expand' withIcon={true} height='32px' marginTop='0px' onClick={expandTable}>
            <Expand />
            <FormattedMessage id='expand' />
          </Button>
        </RightButtons>
      </ReportTableWrapper>
      <ReportsTableWrapper activeTab='siteDetails'>
        <Table
          columns={makeSiteDetailsTabColumns(siteSorting, setSiteSorting, sitesReports)}
          rows={makeSiteDetailsTabRow(sitesReports)}
          noClearFilter={true}
          noResultMessage='noSitesMatchFilteringCriteria'
          dataTest='sitesTab-list'
          isLoading={isSitesLoading}
        />
      </ReportsTableWrapper>
      <ReportsPaginationWrapper>
        <ItemsPerPageDropdown
          currentPage={pagination.currentPage}
          resultsPerPage={pagination.resultsPerPage}
          totalNumber={pagination.totalNumber}
          onChange={changeResultsPerPage}
          dataTest='sites-items-per-page'
          menuPlacement='top'
          alternativeOptions={true}
        />
        {pagination.totalPages > 1 && (
          <Pagination
            activePages={pagination.currentPage}
            numberOfPages={pagination.totalPages}
            nextPage={nextPage}
            prevPage={prevPage}
            nextSection={nextSection}
            prevSection={prevSection}
            selectPage={selectPage}
            dataTest='page-number'
          />
        )}
      </ReportsPaginationWrapper>
    </>
  )
}

export default SitesReportTab
